import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Building2, LogOut } from 'lucide-react';

// Modal obrigatório de escolha de conta no login multi-conta.
// Renderiza sobre o esqueleto do dashboard. Não pode ser fechado sem escolher (só "Sair").
const ClientSelectionModal: React.FC = () => {
  const { pendingSelection, selectClientAtLogin, cancelClientSelection } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [selecting, setSelecting] = useState(false);

  if (!pendingSelection) return null;

  const handleSelect = async (clientId: number) => {
    setSelecting(true);
    try {
      await selectClientAtLogin(clientId, pendingSelection.preSelectToken, setAsDefault);
      // Recarrega para reidratar todo o contexto de dados já autenticado.
      window.location.reload();
    } catch (error: any) {
      toast({
        title: 'Erro ao selecionar conta',
        description: error?.message || 'Tente novamente.',
        variant: 'destructive',
      });
      setSelecting(false);
    }
  };

  const handleSair = () => {
    cancelClientSelection();
    navigate('/login');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="selecao-conta-titulo"
        className="w-full max-w-md rounded-lg border bg-background p-6 shadow-lg"
      >
        <div className="text-center space-y-1.5 mb-4">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <h2 id="selecao-conta-titulo" className="text-xl font-semibold text-foreground">
            Olá! Escolha a conta para continuar
          </h2>
          <p className="text-sm text-muted-foreground">
            Você tem acesso a mais de uma conta. Selecione qual deseja abrir.
          </p>
        </div>

        <div className="space-y-2 max-h-72 overflow-auto">
          {pendingSelection.clients.map((c) => (
            <button
              key={c.id}
              type="button"
              disabled={selecting}
              onClick={() => handleSelect(c.id)}
              className="w-full text-left border border-border rounded-md px-3 py-2 hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50"
            >
              <span className="block text-sm font-medium text-foreground">{c.trade_name || c.legal_name}</span>
              {c.trade_name && <span className="block text-xs text-muted-foreground">{c.legal_name}</span>}
            </button>
          ))}
        </div>

        <div className="flex items-center space-x-2 mt-4">
          <input
            type="checkbox"
            id="modal-set-default"
            checked={setAsDefault}
            disabled={selecting}
            onChange={(e) => setSetAsDefault(e.target.checked)}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <Label htmlFor="modal-set-default" className="text-sm text-muted-foreground">
            Abrir sempre nesta conta
          </Label>
        </div>

        <div className="mt-4 flex items-center justify-between">
          {selecting ? (
            <span className="flex items-center text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Entrando...
            </span>
          ) : (
            <span />
          )}
          <Button type="button" variant="ghost" size="sm" onClick={handleSair} className="gap-2">
            <LogOut className="h-4 w-4" /> Sair
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClientSelectionModal;
